import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FiSend } from 'react-icons/fi';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import toast from 'react-hot-toast';
import { GooSpinner } from 'react-spinners-kit';
import { useTheme } from 'styled-components';

import {
  useBroadcastQuery,
  useWordCloudMutation,
  useWordCloudQuery,
} from '@hooks';
import { CloudData } from '@common/types/api';
import { eventfyStorage } from '@services';

import * as S from './styles';
import { BaseInteractionProps } from '../../types';

type FormInputs = {
  word_cloud: string;
};

type WordSize = 'xl' | 'lg' | 'md' | 'sm' | 'xs';

const getWordSize = (count: number, maxCount: number): WordSize => {
  if (maxCount === 0) return 'md';
  const ratio = count / maxCount;
  if (ratio >= 0.8) return 'xl';
  if (ratio >= 0.5) return 'lg';
  if (ratio >= 0.3) return 'md';
  if (ratio >= 0.15) return 'sm';
  return 'xs';
};

const WordCloud = ({ active: isActive }: BaseInteractionProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const formMethods = useForm<FormInputs>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: yupResolver(
      Yup.object().shape({
        word_cloud: Yup.string()
          .required('valid.word.req')
          .min(2, 'valid.word.invalid')
          .test(
            'string-length',
            'valid.word.onlyOne',
            (value) => (value?.trim().split(' ')?.length || 0) < 2,
          ),
      }),
    ),
  });
  const { handleSubmit, reset } = formMethods;

  const { data: room } = useBroadcastQuery({
    select: ({ id, settings }) => ({
      id,
      isWordCloudActive: !!settings.data.word_cloud,
    }),
    notifyOnChangeProps: ['data'],
  });

  const wordCloudQuery = useWordCloudQuery(room?.id);

  const [amountOfWordsSent, setAmountOfWordsSent] = useState(0);

  const maxWordsToBeSent = wordCloudQuery.data?.word_limit || Infinity;

  const canUserSendNewWord = amountOfWordsSent <= maxWordsToBeSent;

  const cloudTitle =
    wordCloudQuery.data?.description ?? t('pages.broadcast.word.title');

  const cloudWords = useMemo(() => {
    const words = wordCloudQuery.data?.cloud || [];
    if (words.length === 0) return [];

    const maxCount = Math.max(...words.map((w: CloudData) => w.count));

    // Shuffle para distribuir aleatoriamente
    const shuffled = [...words].sort(() => Math.random() - 0.5);

    return shuffled.map((word: CloudData, index: number) => ({
      ...word,
      size: getWordSize(word.count, maxCount),
      index,
    }));
  }, [wordCloudQuery.data]);

  const { mutateAsync, isLoading: isMutating } = useWordCloudMutation(
    room?.id,
    wordCloudQuery.data?.id,
    {
      onSuccess: ({ data }) => {
        toast.success(data.message);

        setAmountOfWordsSent((prevState) => {
          const newAmount = prevState + 1;

          eventfyStorage.setItem(
            `${room?.id}:word_cloud:amount_of_words_sent`,
            newAmount,
          );

          return newAmount;
        });
      },
    },
  );

  const handleWordCloudSubmit = useCallback(
    async ({ word_cloud }: FormInputs) => {
      reset();
      await mutateAsync(word_cloud.toLowerCase());
    },
    [mutateAsync, reset],
  );

  useEffect(() => {
    if (room?.id) {
      const previousAmountOfWordsSent = eventfyStorage.getItem(
        `${room.id}:word_cloud:amount_of_words_sent`,
      );

      setAmountOfWordsSent(Number(previousAmountOfWordsSent || 0));
    }
  }, [room?.id]);

  return (
    <S.Container
      $hide={!room?.isWordCloudActive}
      $disabled={!isActive}
      onSubmit={handleSubmit(handleWordCloudSubmit)}
    >
      <FormProvider {...formMethods}>
        <S.WordCloudWrapper>
          <h3>
            {cloudTitle}

            {isMutating && (
              <GooSpinner
                size={1.5}
                sizeUnit="rem"
                color={theme.colors.brand.primary.default}
              />
            )}
          </h3>

          <S.CloudContainer>
            {cloudWords.map((word) => (
              <S.Word key={word.value} $size={word.size} $index={word.index}>
                {word.value}
              </S.Word>
            ))}
          </S.CloudContainer>
        </S.WordCloudWrapper>

        <S.WordCloudInput
          required
          name="word_cloud"
          type="text"
          autoFocus={isActive}
          readOnly={!room?.isWordCloudActive || !canUserSendNewWord}
          placeholder={
            canUserSendNewWord
              ? t('pages.broadcast.word.title')
              : t('pages.broadcast.word.maxWord')
          }
          buttonIcon={FiSend}
        />
      </FormProvider>
    </S.Container>
  );
};

export default memo(WordCloud);
